import { ChangeEvent } from 'react';
import { Checkbox, Text } from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import { CardCollapsible, InputNumberNimbus, Stack } from 'commons/components';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import Skeleton from './Skeleton';
import { NE_PREMIUM_DELIVERY_OPTIONS } from '../ActiveShippingMethodsCard/utils/ne-premium-delivery-options';

const MODALITIES_WITHOUT_ADD_DAYS = ['sedex_10', 'sedex_12', 'sedex_hoje'];

interface ShippingModalityCardProps {
  code: string;
  name: string;
  isEnabled: boolean;
  allowedFreeShipping: boolean;
  additionalCost: string;
  additionalDays: string;
  onChange: (group: string, e: ChangeEvent<HTMLInputElement>) => void;
  errors?: Partial<Record<string, string>>;
}

function ShippingModalityCard({
  code,
  name,
  isEnabled,
  allowedFreeShipping,
  additionalCost,
  additionalDays,
  onChange,
  errors = {},
}: Readonly<ShippingModalityCardProps>): JSX.Element {
  const t = useTranslationShipping();

  const isNePremiumDeliveryOption = NE_PREMIUM_DELIVERY_OPTIONS.includes(code);

  const handleChangeToggle = (active: boolean) => {
    onChange(code, {
      target: { checked: active, type: 'checkbox', name: 'isEnabled' },
    } as ChangeEvent<HTMLInputElement>);
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(code, e);
  };

  return (
    <CardCollapsible
      title={name}
      active={isEnabled}
      onChange={handleChangeToggle}
      appearance="toggle"
      disabled={isNePremiumDeliveryOption && !isEnabled}
    >
      <Stack column align="stretch">
        <Stack column spacing="tight" align="stretch">
          <FormField label={t('shippingModalities.inputs.additionalCost')}>
            <InputNumberNimbus
              name="additionalCost"
              type="float"
              appendPosition="start"
              append={t('shippingModalities.inputs.prependLabel')}
              value={additionalCost}
              onChange={handleOnChange}
              appearance={errors.additionalCost ? 'danger' : 'neutral'}
            />
          </FormField>
          {!!errors.additionalCost && (
            <Text fontSize="caption" color="danger-textLow">
              {t(errors.additionalCost)}
            </Text>
          )}
        </Stack>
        {!MODALITIES_WITHOUT_ADD_DAYS.includes(code) && (
          <Stack column spacing="tight" align="stretch">
            <FormField label={t('shippingModalities.inputs.additionalDays')}>
              <InputNumberNimbus
                name="additionalDays"
                type="integer"
                value={additionalDays}
                onChange={handleOnChange}
                appearance={errors.additionalDays ? 'danger' : 'neutral'}
              />
            </FormField>
            {!!errors.additionalDays && (
              <Text fontSize="caption" color="danger-textLow">
                {t(errors.additionalDays)}
              </Text>
            )}
          </Stack>
        )}
        <Checkbox
          name="allowedFreeShipping"
          id={`allowedFreeShipping_${code}`}
          label={t('shippingModalities.inputs.allowedFreeShipping', {
            modality: name,
          })}
          checked={allowedFreeShipping}
          onChange={handleOnChange}
        />
      </Stack>
    </CardCollapsible>
  );
}

ShippingModalityCard.Skeleton = Skeleton;

export default ShippingModalityCard;
